import Close from '@mui/icons-material/Close'
import Search from '@mui/icons-material/Search'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Fade from '@mui/material/Fade'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { RouteType } from '../Router/routeConfig'
import QuickSearchInput from './QuickSearchInput'
import QuickSearchNavigationContent from './QuickSearchNavigationContent'
import QuickSearchSubAccountContent from './QuickSearchSubAccountContent'
import QuickSearchTrigger from './QuickSearchTrigger'
import { SubAccountKind, SubAccountSearchQuery, useSubAccountSearchQuery } from '@/generated/graphql'
import useGlobalContext from '@/hooks/useGlobalContext'
import * as routeActions from '@/utils/routeActions'

interface Props {
  searchableContent: RouteType[];
  placeholder?: string;
  debounceMs?: number;
}

type QuickSearchMode = 'navigate' | 'subAccount'

export default function QuickSearch(props: Props): React.ReactElement {
  const { searchableContent, debounceMs = 300 } = props
  const navigate = useNavigate()
  const { currentTaxYear } = useGlobalContext()
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = React.useState(false)

  // Search Input
  const [mode, setMode] = React.useState<QuickSearchMode>('navigate')
  const [query, setQuery] = React.useState('')

  // Navigation State
  const [selectedIndex, setSelectedIndex] = React.useState(0)
  const [groupedResults, setGroupedResults] = React.useState<GroupedResults>({})
  const [flatResults, setFlatResults] = React.useState<RouteType[]>([])

  // SubAccount State
  const [subAccountQuery, setSubAccountQuery] = React.useState('')
  const subAccountSearchQuery = useSubAccountSearchQuery(
    {
      accountNumber: subAccountQuery,
      taxYear: currentTaxYear,
    },
    {
      enabled: Boolean(subAccountQuery.trim()),
    }
  )
  const subAccountSelectedItem = subAccountSearchQuery.data?.subAccountSearch?.result

  const handleOnQueryChange = (value: string): void => {
    setQuery(value)

    if (mode === 'subAccount' && subAccountQuery) {
      setSubAccountQuery('')
    }
  }

  const groupResultsBySection = (results: RouteType[]): GroupedResults => {
    return results.reduce((acc, result) => {
      if (!acc[result.section]) {
        acc[result.section] = []
      }
      acc[result.section].push(result)
      return acc
    }, {} as GroupedResults)
  }

  const performSearch = React.useCallback(
    (searchQuery: string) => {
      if (!searchQuery.trim()) {
        setGroupedResults({})
        setFlatResults([])
        return
      }

      const lowercaseQuery = searchQuery.toLowerCase()

      const filtered = searchableContent.filter((item) => {
        if (item?.children?.length && item?.children?.length > 0) {
          return false
        }

        if (!item.path) {
          return false
        }

        if (item.hideInQuickSearch) {
          return false
        }

        const titleMatch = item?.title?.toLowerCase().includes(lowercaseQuery)
        const descriptionMatch = item.description?.toLowerCase().includes(lowercaseQuery)
        const sectionMatch = item.section?.toLowerCase?.().includes(lowercaseQuery)
        const subsectionMatch = item.subsection?.toLowerCase().includes(lowercaseQuery)
        const keywordMatch = item?.keywords?.includes(lowercaseQuery)

        return titleMatch || descriptionMatch || sectionMatch || subsectionMatch || keywordMatch
      })

      const sortedBySection = filtered.sort((a, b) => {
        const aSectionMatch = a.section?.toLowerCase?.().includes(lowercaseQuery)
        const bSectionMatch = b.section?.toLowerCase?.().includes(lowercaseQuery)

        if (aSectionMatch && !bSectionMatch) {
          return -1
        }

        if (!aSectionMatch && bSectionMatch) {
          return 1
        }

        return 0
      })

      setGroupedResults(groupResultsBySection(sortedBySection))
      setFlatResults(sortedBySection)
      setSelectedIndex(0)
    },
    [searchableContent]
  )

  React.useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query)
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [query, debounceMs, performSearch])

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      const isMod = e.ctrlKey || e.metaKey
      const isK = e.key.toLowerCase() === 'k'

      if (isMod && isK) {
        e.preventDefault()
        setMode(e.shiftKey ? 'subAccount' : 'navigate')
        setIsOpen(true)
      }

      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false)
        setQuery('')
        setSubAccountQuery('')
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return (): void => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])


  const handleKeyDown = (e: React.KeyboardEvent): void => {
    switch (e.key) {
      case 'ArrowDown': {
        if (mode !== 'navigate') {
          return
        }

        e.preventDefault()
        setSelectedIndex((prev) => Math.min(prev + 1, flatResults.length - 1))
        break
      }
      case 'ArrowUp': {
        if (mode !== 'navigate') {
          return
        }

        e.preventDefault()
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
        break
      }
      case 'Enter': {
        e.preventDefault()

        if (mode === 'navigate' && flatResults[selectedIndex]) {
          handleSelect(flatResults[selectedIndex] as RouteType)
        }

        if (mode === 'subAccount' && !subAccountQuery.trim() && query.trim()) {
          setSubAccountQuery(query.trim())
          return
        }

        if (mode === 'subAccount' && subAccountSelectedItem) {
          handleSubAccountSelect(subAccountSelectedItem)
          return
        }
        break
      }
      default:
        break
    }
  }

  const handleSelect = (result: RouteType): void => {
    navigate(result.path as string)
    handleDialogClose()
  }

  const handleDialogClose = (): void => {
    setIsOpen(false)
    setQuery('')
    setSubAccountQuery('')
  }

  const openDialog = (): void => {
    setIsOpen(true)
  }

  const handleOnSelectModeChange = (event: SelectChangeEvent<QuickSearchMode>): void => {
    setMode(event.target.value as QuickSearchMode)
  }

  const handleOnSubAccountSearch = (): void => {
    setSubAccountQuery(query)
  }

  const handleClearSubAccountQuery = (): void => {
    setSubAccountQuery('')
    setQuery('')
  }

  const handleSubAccountSelect = (item: NonNullable<SubAccountSearchQuery['subAccountSearch']['result']>): void => {
    const {
      kind,
      accountNumber,
      leaseMasterAccountNumber,
      telecomAccountNumber,
    } = item

    switch (kind) {
      case SubAccountKind.LeaseMaster:
        routeActions.goToLeaseMasterAccountOverviewPage(navigate, {
          accountNumber: leaseMasterAccountNumber as string,
          taxYear: currentTaxYear,
        })
        break
      case SubAccountKind.TelecomMaster:
        routeActions.goToTelecomOverviewPage(navigate, {
          accountNumber: telecomAccountNumber as string,
          taxYear: currentTaxYear,
        })
        break
      case SubAccountKind.BusinessMaster:
        routeActions.goToAccountOverviewPage(navigate, {
          accountId: accountNumber,
          taxYear: currentTaxYear,
        })
        break
      default:
        return
    }

    handleDialogClose()
  }

  return (
    <>
      <QuickSearchTrigger
        placeholder={t('quickSearchTriggerPlaceholder')}
        onClick={openDialog}
      />


      <Dialog
        open={isOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '80vh',
            bgcolor: 'background.default',
          },
        }}
        TransitionComponent={Fade}
      >
        <QuickSearchInput
          value={query}
          onChange={handleOnQueryChange}
          onKeyDown={handleKeyDown}
          placeholder={t('quickSearchPlaceholder')}
          InputProps={{
            startAdornment: (
              <Select
                sx={{ fontSize: '0.9rem', minWidth: 120 }}
                variant="standard"
                value={mode}
                onChange={handleOnSelectModeChange}
                inputProps={{ 'aria-label': t('quickSearchMode') }}
                disableUnderline
              >
                <MenuItem value="navigate">{t('navigate')}</MenuItem>
                <MenuItem value="subAccount">{t('subAccount')}</MenuItem>
              </Select>
            ),
            endAdornment: (
              mode === 'subAccount' && (
                <>
                  {subAccountQuery && (
                    <IconButton
                      sx={{ mr: 2 }}
                      size="small"
                      onClick={handleClearSubAccountQuery}
                      aria-label={t('clearSearch')}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  )}
                  {subAccountQuery !== query && (
                    <IconButton
                      sx={{ mr: 2 }}
                      size="small"
                      onClick={handleOnSubAccountSearch}
                      aria-label={t('search')}
                    >
                      <Search fontSize="small" />
                    </IconButton>
                  )}
                </>
              )
            ),
          }}
          autoFocus
        />

        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          {mode === 'navigate' && (
            <QuickSearchNavigationContent
              query={query}
              groupedResults={groupedResults}
              flatResults={flatResults}
              selectedIndex={selectedIndex}
              onSelect={handleSelect}
            />
          )}

          {mode === 'subAccount' && (
            <QuickSearchSubAccountContent
              query={query}
              selectedIndex={selectedIndex}
              subAccountQuery={subAccountQuery}
              subAccountSearchQuery={subAccountSearchQuery}
              onSelect={handleSubAccountSelect}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

interface GroupedResults {
  [section: string]: RouteType[];
}
